/**
 * Hook per ricerca chat con cache completa
 *
 * Cerca localmente nelle chat già caricate (con lastMessage).
 * Carica sempre la cache completa (chat + contatti) da WAHA
 * e cerca anche lì per risultati completi.
 * Supporta ricerca per nome, numero di telefono (normalizzato) e ultimo messaggio.
 * Cerca anche per nome del Lead/Cliente abbinato: quello gia' agganciato alla chat
 * (assegnazione) e, nel CRM, i Lead/Clienti col nome cercato il cui numero ha una chat.
 * I Lead/Clienti trovati senza chat tornano a parte, per proporre "Scrivi a ...".
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useWhatsAppChatStore } from '../../stores/whatsappChatStore';
import { useWhatsAppAgentStore } from '../../stores/whatsappAgentStore';
import { supabase } from '../../supabase';
import { searchClientsForChat, type ClientChatCandidate } from '../../services/whatsapp/clientPhoneSearch';
import { trovaChatPerNumero } from '../../services/whatsapp/chatMatch';
import { looksLikePhoneNumber } from '../../services/whatsapp/chatListRules';
import type { ChatOverview } from '../../types/whatsappChat';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/** Rimuove spazi, trattini, parentesi, + per matching numeri telefono */
function normalizePhone(input: string): string {
  return input.replace(/[\s\-\(\)\+]/g, '');
}

/** Verifica se una chat corrisponde alla query di ricerca */
function matchesQuery(chat: { id: string; name: string; lastMessage?: { body: string } }, query: string, normalizedQuery: string): boolean {
  // Match per nome
  if (chat.name.toLowerCase().includes(query)) return true;
  // Match per JID diretto
  if (chat.id.toLowerCase().includes(query)) return true;
  // Match per numero normalizzato (es. "333 123" → "333123" contro "393331234567@c.us")
  if (normalizedQuery.length >= 3) {
    const chatNumber = chat.id.split('@')[0];
    if (chatNumber.includes(normalizedQuery)) return true;
  }
  // Match per ultimo messaggio (solo se disponibile)
  if (chat.lastMessage?.body?.toLowerCase().includes(query)) return true;
  return false;
}

export function useWhatsAppSearch(query: string) {
  const [additionalResults, setAdditionalResults] = useState<ChatOverview[]>([]);
  const [clientMatchIds, setClientMatchIds] = useState<string[]>([]);
  const [clientsWithoutChat, setClientsWithoutChat] = useState<ClientChatCandidate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const loadingRef = useRef(false);
  const runRef = useRef(0);

  // Selector minimo: solo chats per il useMemo dei risultati locali
  const chats = useWhatsAppChatStore((s) => s.chats);
  const assignments = useWhatsAppAgentStore((s) => s.assignments);

  // Effect con SOLO query come dipendenza — il polling delle chat non lo resetta
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const run = ++runRef.current;
    if (!query.trim()) {
      setAdditionalResults([]);
      setClientMatchIds([]);
      setClientsWithoutChat([]);
      setIsSearching(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const q = query.toLowerCase().trim();
      if (q.length < MIN_QUERY_LENGTH) return;

      // Leggi tutto dallo store con getState() (no closure stale)
      const store = useWhatsAppChatStore.getState();
      if (!store.wahaConfig) return;
      setIsSearching(true);

      try {
        // Lead/Clienti col nome cercato (in parallelo al caricamento della cache)
        const clientsPromise: Promise<ClientChatCandidate[]> = looksLikePhoneNumber(q)
          ? Promise.resolve([])
          : supabase.auth.getSession()
              .then(({ data }) => searchClientsForChat(store.wahaConfig!.organizationId, data.session?.user?.id ?? null, query.trim()))
              .catch(() => []);

        // Carica cache se necessario (con guard contro chiamate concorrenti)
        if (!store.allChatsCache && !loadingRef.current) {
          loadingRef.current = true;
          try { await store.loadAllChatsCache(); } finally { loadingRef.current = false; }
        }
        const clients = await clientsPromise;
        if (run !== runRef.current) return; // nel frattempo l'utente ha scritto altro

        const cache = useWhatsAppChatStore.getState().allChatsCache || [];
        const currentChats = useWhatsAppChatStore.getState().chats;
        const loadedIds = new Set(currentChats.map((c) => c.id));
        const normalizedQ = normalizePhone(q);
        const toOverview = (chat: { id: string; name: string; isGroup: boolean }): ChatOverview => ({
          id: chat.id,
          name: chat.name,
          isGroup: chat.isGroup,
          pinned: false,
          archived: false,
        });

        // Cerca nella cache (chat + contatti)
        const cacheMatches = cache
          .filter((chat) => !loadedIds.has(chat.id) && matchesQuery(chat, q, normalizedQ))
          .slice(0, 20)
          .map(toOverview);

        // Chat dei Lead/Clienti trovati per nome; quelli senza chat vanno a parte
        const ids: string[] = [];
        const extra: ChatOverview[] = [];
        const senzaChat: ClientChatCandidate[] = [];
        for (const client of clients) {
          let trovata = false;
          for (const num of client.numbers) {
            const loaded = trovaChatPerNumero(currentChats, num);
            const hit = loaded || trovaChatPerNumero(cache, num);
            if (!hit) continue;
            trovata = true;
            ids.push(hit.id);
            if (!loaded && !cacheMatches.some((c) => c.id === hit.id)) {
              extra.push(toOverview({ ...hit, name: hit.name && !/^\+?\d+$/.test(hit.name) ? hit.name : client.name }));
            }
          }
          if (!trovata && client.numbers.length > 0) senzaChat.push(client);
        }

        setAdditionalResults([...cacheMatches, ...extra]);
        setClientMatchIds(ids);
        setClientsWithoutChat(senzaChat);
      } finally {
        if (run === runRef.current) setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]); // SOLO query — nessun'altra dipendenza che possa resettare il debounce

  // Combina risultati locali + cache
  const results = useMemo(() => {
    if (!query.trim()) return [];

    const q = query.toLowerCase().trim();
    const normalizedQ = normalizePhone(q);
    // Nome del Lead/Cliente gia' agganciato alla chat (assegnazione)
    const byAssignedClient = new Set(
      assignments
        .filter((a) => a.client?.business_name?.toLowerCase().includes(q))
        .map((a) => a.chat_jid),
    );
    const byCrm = new Set(clientMatchIds);
    const local = chats.filter((chat) => matchesQuery(chat, q, normalizedQ) || byAssignedClient.has(chat.id) || byCrm.has(chat.id));

    // Deduplica - aggiungi solo quelli non già presenti
    const ids = new Set(local.map((c) => c.id));
    const additional = additionalResults.filter((c) => !ids.has(c.id));

    return [...local, ...additional];
  }, [query, chats, additionalResults, assignments, clientMatchIds]);

  return { results, isSearching, clientsWithoutChat };
}
